import React, { useState } from 'react'
import { gql, useMutation } from '@apollo/client'
import {
  Box, Dialog, DialogActions, DialogContent, DialogTitle, Grid,
  IconButton, InputAdornment, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material'
import FeeFields, { FEE_QUERY_FIELDS, changedFees, feeErrors, feesToForm, formToFees, needsApproval } from '../PartnerSignups/FeeFields'
import { Icon, T } from '../PartnerSignups/onboardingDesign'
import {
  Badge, Card, CardTitle, FieldLabel, GhostButton, LinkButton, Notice, PrimaryButton,
} from '../PartnerSignups/onboardingUi'

/**
 * The parts the agent portal (views/AgentPortal.jsx) and the ISO portal
 * (views/IsoPortal.jsx) share: the sign-up link, the invitation form, the
 * applications brought in, invitations sent and the fees dialog. An ISO's
 * login is an agent login, so all of these work the same for it.
 */

const REQUEST_FEES = gql`
  mutation RequestMerchantApplicationFees($id: ID!, $fees: MerchantAgreementFeesInput!) {
    requestMerchantApplicationFees(id: $id, fees: $fees) { _id inviteStatus pricing }
  }
`
const SEND_STANDARD = gql`
  mutation SendMerchantApplicationAtStandard($id: ID!) {
    sendMerchantApplicationAtStandard(id: $id) { _id inviteStatus pricing }
  }
`
const SEND_INVITE = gql`
  mutation SendAgentInvite($input: AgentInviteInput!) {
    sendAgentInvite(input: $input) { _id restaurantName email status }
  }
`
export const INVITES = gql`
  query MyAgentInvites {
    myAgentInvites { _id restaurantName contactName email sentAt status }
  }
`
export const STANDARD = gql`query AgentStandardFees { merchantAgreementFees { ${FEE_QUERY_FIELDS} } }`

// Pricing of an application the agent brought in.
const PRICING = {
  STANDARD: ['Standard', 'neutral'],
  CUSTOM: ['Own fees, approved', 'green'],
  PENDING: ['Waiting for approval', 'amber'],
  REJECTED: ['Fees not approved', 'red'],
}

const STATUS = {
  DRAFT: ['Started', 'neutral'],
  NEW: ['Started', 'neutral'],
  CONTACTED: ['Started', 'neutral'],
  STARTED: ['Started', 'neutral'],
  INVITED: ['Invited', 'info'],
  SIGNED: ['Signed, in review', 'purple'],
  ONBOARDED: ['Approved', 'green'],
  APPROVED: ['Approved', 'green'],
  // Credited by a super admin to an onboarded restaurant (backend utils/restaurantCredit.js).
  ASSIGNED: ['Assigned', 'green'],
  REJECTED: ['Not approved', 'red'],
}

export const money = v => `$${(Number(v) || 0).toFixed(2)}`
export const pct = v => `${Number(Number(v || 0).toFixed(2))}%`
export const day = v => (v ? new Date(v).toLocaleDateString() : '—')
export const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`
export const COMMISSION_KIND = { PAID: ['Paid', 'green'], VOID: ['Void', 'neutral'], PENDING: ['Pending', 'amber'] }

// Tables: muted 13px semibold header on the footer tint, outer columns padded
// to the card's edge, no rule under the last row.
export const headCell = { fontWeight: 600, fontSize: 13, color: T.muted, whiteSpace: 'nowrap', bgcolor: T.footerBg }
export const tableSx = {
  '& .MuiTableCell-root': { px: 2 },
  '& .MuiTableCell-body': { py: 1.5 },
  '& .MuiTableCell-root:first-of-type': { pl: { xs: 2, sm: 3 } },
  '& .MuiTableCell-root:last-of-type': { pr: { xs: 2, sm: 3 } },
  '& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-root': { borderBottom: 0 },
}
export const tabSx = { textTransform: 'none', fontWeight: 600, fontSize: 15, color: T.muted, minHeight: 52, '&.Mui-selected': { color: T.text } }

export function StatusBadge({ status }) {
  const [label, kind] = STATUS[status] || [status, 'neutral']
  return <Badge kind={kind}>{label}</Badge>
}

/** One rule in "How it works": an icon tile, a title and its lines. */
export function Rule({ icon: RuleIcon, title, children }) {
  return (
    <Box sx={{ display: 'flex', gap: 1.75, py: 1.75, borderTop: `1px solid ${T.divider}`, '&:first-of-type': { borderTop: 0, pt: 0.5 } }}>
      <Box aria-hidden="true" sx={{ width: 40, height: 40, borderRadius: '12px', background: T.redTint, color: T.redText, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <RuleIcon size={20} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography component="h3" sx={{ fontWeight: 700, fontSize: 15.5, color: T.text, mb: 0.5 }}>{title}</Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.25, color: T.text2, fontSize: 14.5, lineHeight: 1.6, '& li + li': { mt: 0.5 } }}>
          {children}
        </Box>
      </Box>
    </Box>
  )
}

/** The sign-up link with a copy button. */
export function SignupLinkCard({ link, code }) {
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (e) { /* clipboard blocked: the link is selectable */ }
  }
  return (
    <Card>
      <CardTitle sub={<>Share it by text, email or in person. Your code: <strong>{code}</strong></>}>Your sign-up link</CardTitle>
      <TextField
        fullWidth size="small" value={link || ''}
        inputProps={{ 'aria-label': 'Your sign-up link' }}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <Tooltip title={copied ? 'Copied' : 'Copy link'}>
                <IconButton onClick={copy} edge="end" aria-label="Copy link" sx={{ color: copied ? T.green : T.muted }}>
                  {copied ? <Icon.Check size={18} /> : <Icon.Copy size={18} />}
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ),
        }}
        onFocus={e => e.target.select()}
      />
    </Card>
  )
}

/** Email the sign-up link to a restaurant. */
export function InviteCard({ onSent }) {
  const [send, { loading: sending }] = useMutation(SEND_INVITE)
  const [form, setForm] = useState({ restaurantName: '', contactName: '', email: '', phone: '' })
  const [notice, setNotice] = useState(null)
  const [sendError, setSendError] = useState(null)
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }))

  const submit = async () => {
    setNotice(null)
    setSendError(null)
    try {
      await send({ variables: { input: form } })
      setNotice(`Invitation sent to ${form.email}.`)
      setForm({ restaurantName: '', contactName: '', email: '', phone: '' })
      if (onSent) onSent()
    } catch (e) {
      setSendError(e.message)
    }
  }

  return (
    <Card>
      <CardTitle sub="We email them your link to start their application.">Send your link to a restaurant</CardTitle>
      {notice && <Notice severity="success" onClose={() => setNotice(null)}>{notice}</Notice>}
      {sendError && <Notice severity="error" onClose={() => setSendError(null)}>{sendError}</Notice>}
      <Grid container columnSpacing={1.5} rowSpacing={1.75}>
        <Grid item xs={12}>
          <FieldLabel htmlFor="invite-name" label="Restaurant or store name" />
          <TextField id="invite-name" fullWidth size="small" required value={form.restaurantName} onChange={set('restaurantName')} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FieldLabel htmlFor="invite-email" label="Email" />
          <TextField id="invite-email" fullWidth size="small" type="email" required value={form.email} onChange={set('email')} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FieldLabel htmlFor="invite-contact" label="Contact name" optional />
          <TextField id="invite-contact" fullWidth size="small" value={form.contactName} onChange={set('contactName')} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FieldLabel htmlFor="invite-phone" label="Phone" optional />
          <TextField id="invite-phone" fullWidth size="small" type="tel" value={form.phone} onChange={set('phone')} />
        </Grid>
        <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
          <PrimaryButton onClick={submit} busy={sending} disabled={!form.restaurantName || !form.email} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <Icon.Send size={16} />
            Send invitation
          </PrimaryButton>
        </Grid>
      </Grid>
    </Card>
  )
}

/**
 * Set other fees, or send it at the standard ones, for a held application.
 * At or above the minimums the fees apply and it goes to the restaurant;
 * below one, or any other fee changed, a super admin approves first.
 * extraText: what they earn on what's above the minimums (it differs for
 * RushMyFood's agents, ISOs and ISOs' agents).
 */
export function ChangeFeesDialog({ referral, standard, extraText = null, onClose, onDone }) {
  const [form, setForm] = useState(() => feesToForm(referral.fees || standard))
  const [request, { loading: requesting }] = useMutation(REQUEST_FEES)
  const [sendStandard, { loading: sending }] = useMutation(SEND_STANDARD)
  const [error, setError] = useState(null)
  const custom = changedFees(formToFees(form), standard).length > 0
  const approval = custom && needsApproval(formToFees(form), standard)
  const busy = requesting || sending

  const go = async (fn, message) => {
    setError(null)
    try {
      await fn()
      onDone(message)
    } catch (e) {
      setError(e.message)
    }
  }

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Fees for {referral.restaurantName}</DialogTitle>
      <DialogContent dividers sx={{ borderColor: T.divider }}>
        {referral.requestNote && <Notice severity="warning">Not approved: {referral.requestNote}</Notice>}
        <Typography sx={{ color: T.text2, fontSize: 14, mb: 1 }}>
          The order percentages, third-party % and service fee are minimums. Set them at or above the minimum and the application goes straight to the restaurant. Going below a minimum, or changing any other fee, needs a super admin’s approval first.
          {referral.contactEmail ? ` ${referral.contactEmail} is emailed the link once it goes out.` : ''}
        </Typography>
        {extraText && <Typography sx={{ color: T.text2, fontSize: 14, mb: 2 }}>{extraText}</Typography>}
        {error && <Notice severity="error">{error}</Notice>}
        <FeeFields value={form} onChange={setForm} standard={standard} minimums />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, flexWrap: 'wrap', gap: 1 }}>
        <GhostButton onClick={onClose} disabled={busy}>Cancel</GhostButton>
        <Box sx={{ flex: 1 }} />
        <LinkButton
          disabled={busy}
          onClick={() => go(() => sendStandard({ variables: { id: referral._id } }), `Sent to ${referral.contactEmail || 'the restaurant'} at the standard fees.`)}
        >
          Send at standard fees
        </LinkButton>
        <PrimaryButton
          busy={requesting} disabled={busy || !custom || Object.keys(feeErrors(form)).length > 0}
          onClick={() => go(
            () => request({ variables: { id: referral._id, fees: formToFees(form) } }),
            approval ? 'Your fees were sent to a super admin for approval.' : `Sent to ${referral.contactEmail || 'the restaurant'} with your fees.`,
          )}
        >
          {approval ? 'Send fees for approval' : 'Send with these fees'}
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  )
}

/**
 * The applications brought in. agentColumn: an ISO's view, with who brought
 * each one in. commissionHeader: what the last column holds.
 */
export function ReferralsTable({ referrals, standard, onOpen, onChangeFees, agentColumn = false, commissionHeader = 'Commission', emptyText }) {
  if (referrals.length === 0) {
    return <Typography sx={{ p: 5, textAlign: 'center', color: T.hint }}>{emptyText || 'No sign-ups through your link yet.'}</Typography>
  }
  const heads = ['Business', ...(agentColumn ? ['Agent'] : []), 'Status', 'Fees', 'Started', 'Signed', 'Approved', commissionHeader]
  return (
    <Table size="small" sx={{ minWidth: agentColumn ? 980 : 860, ...tableSx }}>
      <TableHead>
        <TableRow>
          {heads.map(h => (
            <TableCell key={h} sx={headCell} align={h === commissionHeader ? 'right' : 'left'}>{h}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {referrals.map(x => {
          const pricing = PRICING[x.pricing]
          const commission = x.commissionAmount == null ? null : COMMISSION_KIND[x.commissionStatus] || COMMISSION_KIND.PENDING
          return (
            <TableRow key={x._id}>
              <TableCell>
                <Typography sx={{ fontWeight: 600, fontSize: 14.5, color: T.text }}>{x.restaurantName}</Typography>
                <Typography sx={{ color: T.hint, fontSize: 13 }}>{x.businessType === 'GROCERY' ? 'Grocery' : 'Restaurant'}</Typography>
                {x.canOpen && onOpen && (
                  <LinkButton size="small" onClick={() => onOpen(x._id)} sx={{ ml: -1, mt: 0.25, minHeight: 30 }}>
                    Open the application
                    <Icon.ArrowRight size={14} />
                  </LinkButton>
                )}
              </TableCell>
              {agentColumn && (
                <TableCell sx={{ color: x.agentName ? T.text : T.hint, whiteSpace: 'nowrap' }}>{x.agentName || 'You'}</TableCell>
              )}
              <TableCell>
                {x.held ? <Badge kind="neutral">Not sent yet</Badge> : <StatusBadge status={x.status} />}
              </TableCell>
              <TableCell>
                {pricing && (
                  <Tooltip title={x.pricing === 'REJECTED' && x.requestNote ? x.requestNote : ''}>
                    <span><Badge kind={pricing[1]}>{pricing[0]}</Badge></span>
                  </Tooltip>
                )}
                {x.canChangeFees && standard && onChangeFees && (
                  <LinkButton size="small" onClick={() => onChangeFees(x)} sx={{ display: 'flex', ml: -1, mt: 0.25, minHeight: 30 }}>
                    Change fees or send
                  </LinkButton>
                )}
              </TableCell>
              <TableCell sx={{ color: T.text2, whiteSpace: 'nowrap' }}>{day(x.startedAt)}</TableCell>
              <TableCell sx={{ color: T.text2, whiteSpace: 'nowrap' }}>{day(x.signedAt)}</TableCell>
              <TableCell sx={{ color: T.text2, whiteSpace: 'nowrap' }}>{day(x.approvedAt)}</TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                {commission ? (
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 14.5 }}>{money(x.commissionAmount)}</Typography>
                    <Badge kind={commission[1]}>{commission[0]}</Badge>
                  </Box>
                ) : '—'}
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}

/** Invitations sent, with how far each restaurant got. */
export function InvitesTable({ invites }) {
  if (invites.length === 0) {
    return <Typography sx={{ p: 5, textAlign: 'center', color: T.hint }}>You have not sent any invitations yet.</Typography>
  }
  return (
    <Table size="small" sx={{ minWidth: 600, ...tableSx }}>
      <TableHead>
        <TableRow>
          {['Business', 'Sent to', 'Sent', 'Progress'].map(h => <TableCell key={h} sx={headCell}>{h}</TableCell>)}
        </TableRow>
      </TableHead>
      <TableBody>
        {invites.map(x => (
          <TableRow key={x._id}>
            <TableCell sx={{ fontWeight: 600 }}>{x.restaurantName}</TableCell>
            <TableCell sx={{ color: T.text2 }}>{[x.contactName, x.email].filter(Boolean).join(' · ')}</TableCell>
            <TableCell sx={{ color: T.text2, whiteSpace: 'nowrap' }}>{day(x.sentAt)}</TableCell>
            <TableCell><StatusBadge status={x.status} /></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
